import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Star, CheckCircle, Shield, BarChart3, MessageSquare, ArrowRight, ChevronRight, Sparkles, Users, TrendingUp, Clock, Mail, Phone, Plug, RefreshCw, CheckCircle2, XCircle, Info, Lightbulb, Menu, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.5 }
};

export default function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [billingYearly, setBillingYearly] = React.useState(true);
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);

  const openLogin = (plan) => navigate(typeof plan === 'string' ? `/app/login?plan=${plan}` : '/app/login', { state: { backgroundLocation: location } });

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="container-main flex items-center justify-between h-16 md:h-32">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Immoverita" className="h-12 md:h-28 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Funktionen</a>
            <a href="#how-it-works" className="text-sm text-muted-foreground hover:text-foreground transition-colors">So funktioniert's</a>
            <a href="#metascore" className="text-sm text-muted-foreground hover:text-foreground transition-colors">MetaScore</a>
            <a href="#preise" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Preise</a>
            <a href="#faq" className="text-sm text-muted-foreground hover:text-foreground transition-colors">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={openLogin} data-testid="landing-header-login-button" className="hidden sm:inline-flex">Login</Button>
            <Button size="sm" className="rounded-xl" onClick={openLogin} data-testid="landing-header-cta-button">
              <span className="hidden sm:inline">Kostenlos starten</span>
              <span className="sm:hidden">Starten</span>
            </Button>
            {/* Hamburger — mobile only */}
            <button
              onClick={() => setMobileNavOpen(o => !o)}
              className="md:hidden p-2 rounded-md hover:bg-muted transition-colors text-muted-foreground"
              aria-label="Navigation öffnen"
            >
              {mobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile nav dropdown */}
        {mobileNavOpen && (
          <div className="md:hidden border-t border-border bg-background/98">
            <nav className="container-main py-3 flex flex-col gap-1">
              {[
                { href: '#features', label: 'Funktionen' },
                { href: '#how-it-works', label: "So funktioniert's" },
                { href: '#metascore', label: 'MetaScore' },
                { href: '#preise', label: 'Preise' },
                { href: '#faq', label: 'FAQ' },
              ].map(({ href, label }) => (
                <a
                  key={href}
                  href={href}
                  onClick={() => setMobileNavOpen(false)}
                  className="px-3 py-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  {label}
                </a>
              ))}
              <div className="pt-2 border-t border-border mt-1">
                <Button variant="ghost" className="w-full justify-start" onClick={() => { setMobileNavOpen(false); openLogin(); }}>
                  Login
                </Button>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="hero-gradient absolute inset-0 noise" />
        <div className="container-main py-16 md:py-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                Aktive Feedbacksteuerung
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-6">
                Die fairsten Bewertungen<br className="hidden sm:block" /> für Ihre Hausverwaltung
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-xl">
                Viele Hausverwaltungen werden online schlechter bewertet, als sie tatsächlich arbeiten.<br />
                <strong className="text-foreground">Immoverita sorgt für ein realistisches und faires Bewertungsbild.</strong>
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="rounded-xl w-full sm:w-auto" onClick={openLogin} data-testid="landing-hero-primary-cta-button">
                  Jetzt faire Bewertungen erhalten
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <a href="#how-it-works">
                  <Button variant="secondary" size="lg" className="rounded-xl w-full sm:w-auto" data-testid="landing-hero-secondary-cta-button">
                    So funktioniert Immoverita
                  </Button>
                </a>
              </div>
            </motion.div>
            
            {/* Interactive Chart Preview */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <Card className="card-shadow p-6">
                <div className="text-center mb-6">
                  <p className="text-sm text-muted-foreground mb-2">Bewertungsvergleich</p>
                  <h3 className="text-lg font-semibold">Durchschnittliche Google-Bewertung</h3>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center p-4 rounded-xl bg-muted/50">
                    <p className="text-xs text-muted-foreground mb-2">Ohne Immoverita</p>
                    <div className="flex justify-center mb-2">
                      {[1,2,3].map(i => <Star key={i} className="w-5 h-5 fill-[hsl(var(--rating-gold))] text-[hsl(var(--rating-gold))]" />)}
                      {[4,5].map(i => <Star key={i} className="w-5 h-5 text-gray-300" />)}
                    </div>
                    <p className="text-3xl font-bold text-muted-foreground">2.8</p>
                    <p className="text-xs text-muted-foreground">12 Bewertungen</p>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-accent/10 border-2 border-accent">
                    <p className="text-xs text-accent font-medium mb-2">Mit Immoverita</p>
                    <div className="flex justify-center mb-2">
                      {[1,2,3,4].map(i => <Star key={i} className="w-5 h-5 fill-[hsl(var(--rating-gold))] text-[hsl(var(--rating-gold))]" />)}
                      {/* 5th star: 30% gold + 70% gray visible */}
                      <span className="relative inline-flex w-5 h-5">
                        <Star className="w-5 h-5 fill-gray-300 text-gray-300 absolute inset-0" />
                        <Star className="w-5 h-5 text-[hsl(var(--rating-gold))] absolute inset-0" style={{ clipPath: 'inset(0 70% 0 0)' }} />
                      </span>
                    </div>
                    <p className="text-3xl font-bold text-foreground">4.3</p>
                    <p className="text-xs text-muted-foreground">89 Bewertungen</p>
                  </div>
                </div>
                <p className="text-center text-sm text-muted-foreground mt-4">
                  Mehr Feedback. Realistischere Bewertungen.
                </p>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="border-y border-border bg-muted/30 py-8">
        <div className="container-main">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: Users, value: '2 Min', label: 'Einrichtungszeit' },
              { icon: MessageSquare, value: '+60%', label: 'Mehr Feedback' },
              { icon: TrendingUp, value: 'KI', label: 'Sachlichkeitsanalyse' },
              { icon: Clock, value: '24/7', label: 'Bewertungslinks aktiv' },
            ].map((stat, i) => (
              <div key={i} className="flex flex-col items-center">
                <stat.icon className="w-6 h-6 text-accent mb-2" />
                <p className="text-2xl font-bold">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Platforms */}
      <section className="py-6 bg-background border-b border-border">
        <div className="container-main">
          <div className="flex flex-col items-center justify-center gap-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Funktioniert mit</p>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground/70">
                <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Google
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-foreground/70">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="12" fill="#00A76F"/>
                  <text x="5" y="16" fontSize="11" fill="white" fontWeight="bold">PE</text>
                </svg>
                ProvenExpert
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-foreground/70">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="24" height="24" rx="4" fill="#00B67A"/>
                  <path d="M12 4l2.1 6.4H20l-5 3.6 1.9 5.9L12 16l-4.9 3.9 1.9-5.9-5-3.6h5.9L12 4z" fill="white"/>
                </svg>
                Trustpilot
              </div>
              <span className="text-sm text-muted-foreground">und mehr</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="section-padding bg-background" id="problem">
        <div className="container-main">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">
              Warum Hausverwaltungen online oft unfair bewertet werden
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'Unzufriedene bewerten häufiger',
                description: 'Zufriedene Kunden schreiben selten Bewertungen. Ergebnis: Online entsteht ein verzerrtes Bild.',
                icon: MessageSquare
              },
              {
                title: 'Ein Konflikt = 1-Sterne-Bewertung',
                description: 'Ein einzelner Konflikt erzeugt sofort 1-Sterne-Bewertungen. Auch wenn 90% der Arbeit hervorragend ist.',
                icon: Star
              },
              {
                title: 'Emotionale Bewertungen',
                description: 'Viele Bewertungen sind emotional oder rechtlich riskant: Beleidigungen, falsche Behauptungen, Frustbewertungen.',
                icon: Shield
              }
            ].map((problem, i) => (
              <Card key={i} className="p-6">
                <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center mb-4">
                  <problem.icon className="w-6 h-6 text-destructive" />
                </div>
                <h3 className="font-semibold mb-2">{problem.title}</h3>
                <p className="text-sm text-muted-foreground">{problem.description}</p>
              </Card>
            ))}
          </div>
          <div className="mt-8 rounded-xl p-5 flex items-start gap-4"
               style={{
                 background: 'linear-gradient(135deg, #f0fdf8 0%, #eff6ff 100%) padding-box, linear-gradient(135deg, rgba(16,185,129,0.3), rgba(59,130,246,0.3)) border-box',
                 border: '2px solid transparent',
               }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-sm"
                 style={{ background: 'linear-gradient(135deg, #3b82f6, #10b981)' }}>
              <Lightbulb className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-700 mb-2">
                Das Internet zeigt oft nicht die Realität Ihrer Arbeit.
              </p>
              <p className="text-sm text-slate-500 leading-relaxed">
                Das ist wissenschaftlich belegt: Der sogenannte <strong className="text-slate-600">Negativitätsbias</strong> beschreibt, dass Menschen negative Erlebnisse stärker wahrnehmen und häufiger teilen als positive. Studien zeigen, dass unzufriedene Kunden bis zu 3x häufiger Bewertungen schreiben.
              </p>
              <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-400">
                <a href="https://www.researchgate.net/publication/46608952_Bad_Is_Stronger_than_Good" target="_blank" rel="noopener noreferrer" className="hover:text-blue-500 underline underline-offset-2 transition-colors">
                  Baumeister et al. (2001): Bad Is Stronger Than Good
                </a>
                <a href="https://doi.org/10.1016/j.jretconser.2014.06.002" target="_blank" rel="noopener noreferrer" className="hover:text-blue-500 underline underline-offset-2 transition-colors">
                  Negative Bias in Online Reviews (Journal of Retailing)
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="section-padding bg-muted/30" id="features">
        <div className="container-main">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">
              Immoverita schafft faire Bewertungen
            </h2>
            <p className="text-muted-foreground">
              Schlechte Bewertungen entstehen von selbst.<br />
              <strong className="text-foreground">Gute Bewertungen muss man aktiv steuern.</strong>
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'Aktive Feedbacksteuerung',
                description: 'Nicht warten, bis Bewertungen entstehen – sondern Feedback aktiv einholen.',
                icon: BarChart3
              },
              {
                title: 'Sachlichkeitsindex',
                description: 'Immoverita analysiert Bewertungen: Emotion, Sachlichkeit, Risiko -- transparent und nachvollziehbar.',
                icon: Shield
              },
              {
                title: 'KI-Unterstützung',
                description: 'Bewertungen können professionell umformuliert werden. Fair. Neutral. Sachlich.',
                icon: Sparkles
              },
              {
                title: 'Verifizierte Bewertungen',
                description: 'Bewertungen können verifiziert werden. Das erhöht Vertrauen.',
                icon: CheckCircle
              }
            ].map((feature, i) => (
              <Card key={i} className="p-6 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-accent" />
                </div>
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="section-padding bg-background" id="how-it-works">
        <div className="container-main">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">
              So funktioniert Immoverita
            </h2>
            <p className="text-muted-foreground">
              In wenigen Minuten startklar
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '1',
                title: 'Bewertungslink teilen',
                description: 'Sie erhalten einen individuellen Bewertungslink. Platzieren Sie ihn in E-Mail-Signatur, Portal, Website oder als QR-Code.'
              },
              {
                step: '2',
                title: 'Kunden geben Feedback',
                description: 'Eigentümer und Bewerter können einfach bewerten — aufgeteilt in Unterthemen. So entsteht ein differenziertes Bild statt einer pauschalen Note.'
              },
              {
                step: '3',
                title: 'Faire Bewertungen sichtbar',
                description: 'Immoverita hilft Ihren Kunden, faire und sachliche Bewertungen zu formulieren. So entsteht ein realistisches Bild in der Öffentlichkeit.'
              },
              {
                step: '4',
                title: 'Feedback aktiv managen',
                description: 'Als Hausverwaltung haben Sie die Chance, auf Kritik zu reagieren und sich für Lob zu bedanken. Mit Immoverita behalten Sie den Überblick.'
              }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="section-padding bg-muted/30">
        <div className="container-main">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">
              Nahtlose Integration in Ihren Arbeitsalltag <span className="text-lg font-normal text-muted-foreground">(geplant)</span>
            </h2>
            <p className="text-muted-foreground">
              Bei Immoverita arbeiten wir daran, Ihr bestehendes Hausverwaltungs-Portal oder Ticketing-System direkt anzubinden.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6">
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                <Plug className="w-6 h-6 text-accent" />
              </div>
              <h3 className="font-semibold mb-2">Portal-Anbindung</h3>
              <p className="text-sm text-muted-foreground">
                Immoverita wird sich nahtlos an Ihr bestehendes Portal und Vorgangsmanagement anbinden lassen.
              </p>
            </Card>
            <Card className="p-6">
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                <RefreshCw className="w-6 h-6 text-accent" />
              </div>
              <h3 className="font-semibold mb-2">Automatische Bewertungseinladung</h3>
              <p className="text-sm text-muted-foreground">
                Sobald ein Vorgang geschlossen wird, soll der Eigentümer oder Mieter automatisch eine Einladung zur Bewertung erhalten.
              </p>
            </Card>
            <Card className="p-6">
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-accent" />
              </div>
              <h3 className="font-semibold mb-2">KI-gestützte Sentiment-Analyse</h3>
              <p className="text-sm text-muted-foreground">
                Zufriedene Kunden sollen durch KI-Analyse erkannt und gezielt zur öffentlichen Bewertung ermutigt werden.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* MetaScore Section */}
      <section className="section-padding bg-background" id="metascore">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <BarChart3 className="w-4 h-4" />
                Powered by Immoverita
              </div>
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">
                Der MetaScore: Ihr Bewertungsindex auf einen Blick
              </h2>
              <p className="text-muted-foreground mb-6">
                Der MetaScore bündelt alle Ihre Bewertungen von verschiedenen Plattformen zu einem einzigen, sauberen Gesamtwert. Duplikate werden automatisch erkannt und herausgefiltert — damit der Wert aussagekräftig und fair bleibt.
              </p>
              <div className="space-y-4">
                {[
                  { label: 'Immoverita', desc: 'Direkt erfasste Bewertungen über Ihre personalisierten Bewertungslinks.' },
                  { label: 'Google', desc: 'Ihre Google-Bewertungen fließen automatisch in den MetaScore ein.' },
                  { label: 'ProvenExpert', desc: 'Bewertungen von ProvenExpert werden einbezogen und gewichtet.' },
                  { label: 'Trustpilot', desc: 'Auch Trustpilot-Bewertungen werden berücksichtigt.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-accent" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{item.label}</p>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="card-shadow p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">MetaScore</p>
                    <p className="text-4xl font-bold">4.2<span className="text-xl text-muted-foreground">/5</span></p>
                  </div>
                  <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center">
                    <TrendingUp className="w-8 h-8 text-accent" />
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { label: 'Immoverita', value: 92 },
                    { label: 'Google', value: 78 },
                    { label: 'ProvenExpert', value: 85 },
                    { label: 'Trustpilot', value: 70 },
                  ].map((item) => (
                    <div key={item.label}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">{item.label}</span>
                        <span className="font-medium">{item.value}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-accent"
                          style={{ width: `${item.value}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground text-center mt-4">Beispiel-Dashboard · Powered by Immoverita</p>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Fairness Section */}
      <section className="section-padding bg-primary text-primary-foreground">
        <div className="container-main">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">
              Fair für alle Beteiligten
            </h2>
            <p className="opacity-90 mb-8">
              Immoverita schützt Hausverwaltungen durch differenzierte Bewertungen, 
              Eigentümer und Mieter durch konstruktive Feedbackmöglichkeiten
              und die Öffentlichkeit durch ein realistisches Bewertungsbild.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: 'Hausverwaltungen', desc: 'Differenzierte Bewertungen' },
                { title: 'Eigentümer & Mieter', desc: 'Konstruktives Feedback' },
                { title: 'Öffentlichkeit', desc: 'Realistisches Bild' }
              ].map((item, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/10">
                  <CheckCircle className="w-6 h-6 mx-auto mb-2" />
                  <p className="font-semibold">{item.title}</p>
                  <p className="text-sm opacity-80">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Besser mit Immoverita */}
      <section className="section-padding bg-muted/30">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-3">
              Besser mit Immoverita
            </h2>
            <p className="text-muted-foreground">
              Wie Hausverwaltungen Bewertungen heute erhalten — und wie es besser geht.
            </p>
          </motion.div>

          <motion.div {...fadeInUp} className="max-w-4xl mx-auto overflow-hidden rounded-xl border border-border shadow-sm">
            {/* Table header */}
            <div className="grid grid-cols-2">
              <div className="bg-destructive/10 border-r border-border px-3 sm:px-5 py-4 flex items-center gap-2">
                <XCircle className="w-5 h-5 text-destructive shrink-0" />
                <span className="font-semibold text-destructive text-xs sm:text-sm">Ohne Immoverita</span>
              </div>
              <div className="bg-primary/5 px-3 sm:px-5 py-4 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                <span className="font-semibold text-primary text-xs sm:text-sm">Mit Immoverita</span>
              </div>
            </div>
            {/* Rows */}
            {[
              [
                'Verärgerte Eigentümer & Mieter bewerten impulsiv — sachliche Stimmen schweigen',
                'Impuls-Check dämpft den Negativitätsbias — differenziertes Feedback statt Frust'
              ],
              [
                'Hürde zu hoch: Bewerter müssen Text selbst verfassen — viele brechen ab oder schreiben kaum etwas',
                'KI-Textvorschläge generieren fertige Bewertungstexte — mehr vollständige Bewertungen'
              ],
              [
                'Negative Bewertungen landen sofort öffentlich bei Google',
                'Kritisches Feedback kann adressiert werden — bevor es eskaliert'
              ],
              [
                'Kein Überblick: woher kommen Bewertungen?',
                'Pro Kanal & Standort: Sie sehen genau, woher Bewertungen kommen'
              ],
              [
                'Keine Chance, Probleme intern zu lösen bevor sie eskalieren',
                'Interne Problemlösung vor Eskalation'
              ],
              [
                'Durchschnitt wird durch einzelne Ausreißer verzerrt',
                'KI-Sachlichkeitsindex filtert emotionale Ausreißer heraus'
              ],
            ].map(([left, right], i) => (
              <div key={i} className={`grid grid-cols-2 border-t border-border ${i % 2 === 1 ? 'bg-muted/20' : ''}`}>
                <div className="px-3 sm:px-5 py-3 sm:py-3.5 flex gap-2 text-xs sm:text-sm text-muted-foreground border-r border-border">
                  <span className="text-destructive/60 font-bold mt-0.5 shrink-0">✕</span>
                  {left}
                </div>
                <div className="px-3 sm:px-5 py-3 sm:py-3.5 flex gap-2 text-xs sm:text-sm text-foreground">
                  <span className="text-primary font-bold mt-0.5 shrink-0">✓</span>
                  {right}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="section-padding bg-background" id="preise">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">
              Einfache, transparente Preise
            </h2>
            <p className="text-muted-foreground">
              Starten Sie kostenlos. Upgraden Sie, wenn Sie mehr brauchen.
            </p>
          </div>
          {(() => {
            const METASCORE_TIP = 'Der MetaScore kombiniert Ihre Bewertungen von Immoverita, Google, ProvenExpert und Trustpilot zu einem bereinigten Gesamtwert — Duplikate werden automatisch herausgefiltert.';
            const SACHLICHKEIT_TIP = 'Erkennt emotionale Formulierungen in Echtzeit: „Moment mal — was Sie gerade schreiben wirkt sehr aufgeladen und kann angefochten werden. Möchten Sie es sachlicher formulieren?" Schützt Sie vor rechtlich riskanten Bewertungen.';

            // Master feature list — Gruppen-Segmentierung entfernt
            const FEATURES = [
              { label: 'Bewertungslinks',                  tip: null },
              { label: 'KI-Textvorschläge',                tip: null },
              { label: 'Logo & Markenfarben',              tip: null },
              { label: 'CSV-Export & Funnel-Statistiken',  tip: null },
              { label: 'KI-Sachlichkeitsindex',            tip: SACHLICHKEIT_TIP },
              { label: 'MetaScore',                        tip: METASCORE_TIP },
              { label: 'Kein Immoverita-Branding',         tip: null },
              { label: 'Custom Domain',                    tip: null },
              { label: 'Custom Design & eigenes CSS',      tip: null },
              { label: 'Automatische Antworten',           tip: null },
              { label: 'Dedizierter Ansprechpartner',      tip: null },
            ];

            const PLANS = [
              {
                name: 'Kostenlos',
                sub: 'Für den Einstieg',
                price_m: '0 €',
                price_y: '0 €',
                period_m: null,
                period_y: null,
                save_badge: null,
                highlight: false,
                badge: null,
                cta: 'Kostenlos starten',
                href: '/onboarding',
                branding: true,
                values: {
                  'Bewertungslinks':                  '1 Bewertungslink',
                  'KI-Textvorschläge':                false,
                  'Logo & Markenfarben':              false,
                  'CSV-Export & Funnel-Statistiken':  false,
                  'KI-Sachlichkeitsindex':            false,
                  'MetaScore':                        false,
                  'Kein Immoverita-Branding':         null,
                  'Custom Domain':                    null,
                  'Custom Design & eigenes CSS':      null,
                  'Automatische Antworten':           null,
                  'Dedizierter Ansprechpartner':      null,
                },
              },
              {
                name: 'Standard',
                sub: 'Für wachsende HVs',
                price_m: '14,90 €',
                price_m_equiv: '11,18 €',
                price_y_total: '134,10 €',
                period_m: '/ Monat',
                save_badge: '3 Monate gratis',
                highlight: false,
                badge: null,
                cta: 'Jetzt starten',
                href: '/onboarding?plan=standard',
                branding: true,
                values: {
                  'Bewertungslinks':                  'Unbegrenzt',
                  'KI-Textvorschläge':                true,
                  'Logo & Markenfarben':              true,
                  'CSV-Export & Funnel-Statistiken':  false,
                  'KI-Sachlichkeitsindex':            false,
                  'MetaScore':                        false,
                  'Kein Immoverita-Branding':         null,
                  'Custom Domain':                    null,
                  'Custom Design & eigenes CSS':      null,
                  'Automatische Antworten':           null,
                  'Dedizierter Ansprechpartner':      null,
                },
              },
              {
                name: 'Premium',
                sub: 'Alles inklusive',
                price_m: '24,90 €',
                price_m_equiv: '18,68 €',
                price_y_total: '224,10 €',
                period_m: '/ Monat',
                save_badge: '3 Monate gratis',
                highlight: true,
                badge: 'Empfohlen',
                cta: 'Jetzt starten',
                href: '/onboarding?plan=premium',
                branding: false,
                values: {
                  'Bewertungslinks':                  'Unbegrenzt',
                  'KI-Textvorschläge':                true,
                  'Logo & Markenfarben':              true,
                  'CSV-Export & Funnel-Statistiken':  true,
                  'KI-Sachlichkeitsindex':            true,
                  'MetaScore':                        true,
                  'Kein Immoverita-Branding':         true,
                  'Custom Domain':                    null,
                  'Custom Design & eigenes CSS':      null,
                  'Automatische Antworten':           null,
                  'Dedizierter Ansprechpartner':      null,
                },
              },
              {
                name: 'Enterprise',
                sub: 'Das Maximale rausholen',
                price_m: 'ab 99,00 €',
                price_m_equiv: 'ab 74,25 €',
                price_y_total: 'ab 891,00 €',
                period_m: '/ Monat',
                save_badge: '3 Monate gratis',
                highlight: false,
                badge: null,
                cta: 'Kontakt aufnehmen',
                href: '/kontakt',
                branding: false,
                values: {
                  'Bewertungslinks':                  'Unbegrenzt',
                  'KI-Textvorschläge':                true,
                  'Logo & Markenfarben':              true,
                  'CSV-Export & Funnel-Statistiken':  true,
                  'KI-Sachlichkeitsindex':            true,
                  'MetaScore':                        true,
                  'Kein Immoverita-Branding':         true,
                  'Custom Domain':                    true,
                  'Custom Design & eigenes CSS':      true,
                  'Automatische Antworten':           true,
                  'Dedizierter Ansprechpartner':      true,
                },
              },
            ];

            const FeatureRow = ({ label, tip, value }) => {
              if (value === null) return null;
              const included = value === true || typeof value === 'string';
              return (
                <li className={`flex items-center gap-2 text-sm py-0.5 ${included ? '' : 'text-muted-foreground'}`}>
                  {included
                    ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                    : <XCircle className="w-4 h-4 text-muted-foreground/40 shrink-0" />
                  }
                  {typeof value === 'string'
                    ? <span><span className="font-medium">{value}</span> {label}</span>
                    : <span>{label}</span>
                  }
                  {tip && (
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button className="text-muted-foreground hover:text-foreground transition-colors shrink-0">
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[200px] text-xs bg-foreground text-background">
                          {tip}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </li>
              );
            };

            const price = (plan) => billingYearly && plan.price_m_equiv ? plan.price_m_equiv : plan.price_m;
            const period = () => '/ Monat';

            return (
              <>
              {/* Billing toggle */}
              <div className="flex items-center justify-center gap-3 mb-8">
                <span className={`text-sm ${!billingYearly ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>Monatlich</span>
                <button
                  onClick={() => setBillingYearly(v => !v)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${billingYearly ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${billingYearly ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className={`text-sm ${billingYearly ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                  Jährlich
                  <span className="ml-1.5 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">3 Monate gratis</span>
                </span>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {PLANS.map((plan) => (
                  <Card key={plan.name} className={`p-6 flex flex-col relative ${plan.highlight ? 'border-primary bg-primary/5' : ''}`}>
                    {plan.badge && (
                      <div className="absolute top-4 right-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary text-primary-foreground">
                          {plan.badge}
                        </span>
                      </div>
                    )}
                    <div className="mb-6">
                      <p className="text-sm text-muted-foreground mb-1">{plan.sub}</p>
                      <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
                      <div className="flex items-baseline gap-1 flex-wrap">
                        <span className="text-2xl font-bold">{price(plan)}</span>
                        <span className="text-sm text-muted-foreground">{period(plan)}</span>
                      </div>
                      {billingYearly && plan.price_y_total && plan.price_y_total !== '0 €' ? (
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <span className="text-xs text-muted-foreground">{plan.price_y_total} / Jahr</span>
                          <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">{plan.save_badge}</span>
                        </div>
                      ) : !billingYearly && plan.save_badge && (
                        <span className="text-xs text-muted-foreground mt-1 block">oder jährlich sparen</span>
                      )}
                    </div>
                    <ul className="space-y-2.5 mb-8 flex-1">
                      {FEATURES.map((f) => (
                        <FeatureRow key={f.label} label={f.label} tip={f.tip} value={plan.values[f.label]} />
                      ))}
                    </ul>
                    {plan.branding && (
                      <p className="text-xs text-muted-foreground mb-4">Powered by Immoverita Branding</p>
                    )}
                    {plan.href === '/kontakt' ? (
                      <Link to="/kontakt">
                        <Button variant="outline" className="w-full rounded-xl">
                          {plan.cta}
                        </Button>
                      </Link>
                    ) : (
                      <Button
                        variant={plan.highlight ? 'default' : 'outline'}
                        className="w-full rounded-xl"
                        onClick={() => openLogin(new URLSearchParams((plan.href || '').split('?')[1] || '').get('plan') || undefined)}
                      >
                        {plan.cta}
                      </Button>
                    )}
                  </Card>
                ))}
              </div>
              <p className="text-center text-xs text-muted-foreground mt-4">Alle Preise zzgl. 19 % MwSt.</p>
              </>
            );
          })()}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-background" id="faq">
        <div className="container-main">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">
              Häufig gestellte Fragen
            </h2>
          </div>
          <div className="max-w-2xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {[
                {
                  q: 'Funktioniert Immoverita mit meinen bestehenden Tools?',
                  a: 'Immoverita ist ein eigenständiges Tool und funktioniert unabhängig von Ihrer bestehenden Software. Dank der Standalone-Architektur lässt es sich in Kombination mit jedem System einsetzen — ob Outlook, Google Mail oder einem Ticketsystem wie casavi, facilioo, etg24, iDWELL, DoNexus oder Immila. Kein Systemwechsel, kein Integrationsaufwand, keine Einschränkung.'
                },
                {
                  q: 'Welche Voraussetzungen brauche ich für Immoverita?',
                  a: 'Unabhängig von Ihrem Tool-Setup können Sie innerhalb von 2 Minuten kostenlos und ohne Zahlungsdaten starten. Sie benötigen lediglich eine E-Mail-Adresse. Der Start erfordert keine technischen Vorkenntnisse — einfach mal ausprobieren. Give it a try.'
                },
                {
                  q: 'Für welche Hausverwaltungen eignet sich Immoverita?',
                  a: 'Immoverita eignet sich für Hausverwaltungen jeder Größe und jeder Art - von der Einzelverwaltung bis zum Unternehmen mit mehreren Standorten. Die Bewertungslinks können pro Standort, Team und Bewertergruppe (Eigentümer, Mieter, …) individualisiert werden.'
                },
                {
                  q: 'Ich habe bereits gute Bewertungen. Wie profitiere ich trotzdem von Immoverita?',
                  a: 'Sie stimmen uns sicher zu: zu viele gute Bewertungen kann man nicht haben. Aber niemand weiß, was die Zukunft bringt — ein unzufriedener Eigentümer oder Mieter, ein eskalierter Konflikt, ein schlechter Tag. Oder ein Handwerker, der schlicht nicht zum vereinbarten Termin erscheint: Die Hausverwaltung bekommt die schlechte Bewertung, obwohl sie nichts dafür kann.\n\nEine starke Bewertungsbasis ist Ihr Puffer. Wer heute viele gute Bewertungen hat, kann sich morgen eine schlechte leisten, ohne dass es das Gesamtbild kippt. Immoverita hilft Ihnen, diesen Puffer systematisch aufzubauen.'
                },
                {
                  q: 'Wieso ist Immoverita besser als mein Google-Bewertungslink?',
                  a: 'Ein reiner Google-Link sammelt ungefiltertes Feedback — ohne Kontext, ohne Differenzierung, ohne Chance zur Reaktion. Immoverita geht weiter:\n\n• Bewertungen werden in Unterthemen aufgeteilt (Kommunikation, Service, Reaktionszeit, …) — so entsteht ein differenziertes Bild statt einer pauschalen Note.\n• Ein integrierter Sachlichkeitsindex hilft Bewertern, Feedback fair und konstruktiv zu formulieren.\n• Kritisches Feedback kann ans Vorgangsmanagement übergeben werden, bevor eine unfaire Bewertung öffentlich wird, damit gezielt und strukturiert darauf eingegangen werden kann.\n• Sie behalten den Überblick über alle Bewertungen in einem Dashboard und können aktiv steuern.'
                },
                {
                  q: 'Ich habe Angst, dass ich dadurch noch mehr schlechte Bewertungen bekomme. Wie hilft mir Immoverita dabei?',
                  a: 'Im Gegenteil. Immoverita analysiert jede Bewertung mit einem Sachlichkeitsindex und bietet Bewertenden die Möglichkeit, impulsive Texte sachlich umzuformulieren. Kritik wird nicht gesteuert, sondern fair und konstruktiv gestaltet.'
                },
              ].map((faq, i) => (
                <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-accordion-item-${i}`}>
                  <AccordionTrigger className="text-left" data-testid={`faq-accordion-item-${i}-trigger`}>
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground whitespace-pre-line">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-muted/30">
        <div className="container-main">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">
              Sorgen Sie für faire Bewertungen Ihrer Hausverwaltung
            </h2>
            <p className="text-muted-foreground mb-8">
              Starten Sie jetzt mit Immoverita.
            </p>
            <Button size="lg" className="rounded-xl" onClick={openLogin} data-testid="landing-final-cta-button">
              Jetzt faire Bewertungen erhalten
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Demo Access Section - only in development */}
      {process.env.NODE_ENV === 'development' && (
        <section className="py-8 bg-yellow-50 border-y-2 border-yellow-300">
          <div className="container-main">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-yellow-400 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-yellow-800" />
              </div>
              <h3 className="font-semibold text-yellow-800">Demo & Test-Zugang</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <Link to="/r/demo-hausverwaltung/hauptseite">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer border-yellow-300 bg-white">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <Star className="w-5 h-5 text-yellow-600" />
                      <div>
                        <p className="font-medium">Review Funnel testen</p>
                        <p className="text-xs text-muted-foreground">/r/demo-hausverwaltung/hauptseite</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/app/login?demo=hv">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer border-yellow-300 bg-white">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium">HV Dashboard</p>
                        <p className="text-xs text-muted-foreground">demo@hausverwaltung.de</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/admin/login?demo=admin">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer border-yellow-300 bg-white">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-purple-600" />
                      <div>
                        <p className="font-medium">Admin Dashboard</p>
                        <p className="text-xs text-muted-foreground">admin@immoverita.de</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground py-12">
        <div className="container-main">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="Immoverita" className="h-12 md:h-20 w-auto brightness-0 invert" />
              </div>
              <p className="text-sm opacity-80">
                Faire Bewertungen für Hausverwaltungen.<br />Eine Lösung des Instituts für Standards & Qualität in der Immobilienwirtschaft.
              </p>
            </div>
            <div />
            <div />
            <div>
              <ul className="space-y-2 text-sm opacity-80">
                <li>
                  <Link to="/kontakt" className="hover:opacity-100 underline underline-offset-4">
                    Kontakt
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm opacity-60">
              © {new Date().getFullYear()} Immoverita. Alle Rechte vorbehalten.
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm opacity-60">
              <Link to="/impressum" className="hover:opacity-100">Impressum</Link>
              <Link to="/datenschutz" className="hover:opacity-100">Datenschutz</Link>
              <Link to="/agb" className="hover:opacity-100">AGB</Link>
              <Link to="/ki-transparenz" className="hover:opacity-100">KI-Transparenz</Link>
              <Link to="/barrierefreiheit" className="hover:opacity-100">Barrierefreiheit</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
